import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";

export const CommentThread = ({ entityType, entityId, onPosted }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [mentioned, setMentioned] = useState([]); // [{user_id,name}]
  const [suggest, setSuggest] = useState(null); // {query, options}
  const taRef = useRef(null);

  const load = () => api.get("/comments", { params: { entity_type: entityType, entity_id: entityId } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { if (entityId) load(); }, [entityType, entityId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const onChange = (e) => {
    const v = e.target.value; setText(v);
    const m = v.slice(0, e.target.selectionStart).match(/@([\w.\- ]{0,30})$/);
    if (m) {
      const q = m[1].toLowerCase();
      const opts = users.filter((u) => (u.name || u.email || "").toLowerCase().includes(q)).slice(0, 6);
      setSuggest(opts.length ? { query: m[1], options: opts } : null);
    } else setSuggest(null);
  };
  const pickMention = (u) => {
    const nm = u.name || u.email;
    const re = /@([\w.\- ]{0,30})$/;
    setText((t) => t.replace(re, `@${nm} `));
    setMentioned((mm) => (mm.find((x) => x.user_id === u.user_id) ? mm : [...mm, { user_id: u.user_id, name: nm }]));
    setSuggest(null);
    taRef.current?.focus();
  };

  const add = async () => {
    const t = text.trim(); if (!t) return; setBusy(true);
    const mentions = mentioned.filter((m) => t.includes(`@${m.name}`)).map((m) => m.user_id);
    try {
      const r = await api.post("/comments", { entity_type: entityType, entity_id: entityId, text: t, mentions });
      setItems((i) => [...i, r.data]); setText(""); setMentioned([]); setSuggest(null);
      onPosted && onPosted();
      if (mentions.length) toast.success(`Notified ${mentions.length} teammate${mentions.length > 1 ? "s" : ""}`);
    } catch { toast.error("Could not post comment"); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/comments/${id}`); setItems((i) => i.filter((x) => x.comment_id !== id)); onPosted && onPosted(); } catch { toast.error("Could not delete"); } };

  const renderText = (txt) => txt.split(/(@[\w.\-]+(?:\s[\w.\-]+)?)/g).map((p, i) =>
    p.startsWith("@") ? <span key={i} className="text-[#003E87] font-medium">{p}</span> : <span key={i}>{p}</span>);

  return (
    <div data-testid={`comments-${entityType}`}>
      <div className="space-y-3 max-h-72 overflow-auto pr-1">
        {items.length === 0 && <div className="text-xs text-slate-400">No comments yet. Start the thread below.</div>}
        {items.map((c) => (
          <div key={c.comment_id} className="group flex gap-2.5" data-testid={`comment-${c.comment_id}`}>
            <div className="w-7 h-7 rounded-full bg-[#003E87]/10 text-[#003E87] grid place-items-center text-[11px] font-semibold shrink-0">
              {(c.author_name || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-slate-400">{c.author_name}{c.via === "claude" ? " via Claude" : ""} · {fmtDate(c.created_at)}</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap break-words">{renderText(c.text)}</div>
            </div>
            {(c.author_id === user?.user_id || ["owner", "admin"].includes(user?.role)) && (
              <button onClick={() => del(c.comment_id)} className="text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100 shrink-0" title="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="relative mt-3 pt-3 border-t border-slate-100">
        {suggest && (
          <div className="absolute bottom-full mb-1 left-0 w-64 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-20" data-testid="mention-suggest">
            {suggest.options.map((u) => (
              <button key={u.user_id} onClick={() => pickMention(u)} data-testid={`mention-opt-${u.user_id}`}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-[#003E87]/10 text-[#003E87] grid place-items-center text-[10px] font-semibold">{(u.name || u.email).slice(0, 2).toUpperCase()}</span>
                <span className="truncate">{u.name || u.email}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea ref={taRef} value={text} onChange={onChange} rows={2} placeholder="Add a comment… type @ to mention a teammate"
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) add(); if (e.key === "Escape") setSuggest(null); }}
            className="flex-1 text-sm border border-slate-200 rounded px-3 py-2 resize-none" data-testid="comment-input" />
          <button onClick={add} disabled={busy || !text.trim()} data-testid="comment-send"
            className="h-9 px-3 rounded bg-[#003E87] text-white text-sm flex items-center gap-1.5 disabled:opacity-40">
            <Send className="w-3.5 h-3.5" /> Post
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommentThread;

